
import { defineComponent } from "vue";
import { courseIdMixin } from "@/mixins";
import Btn from "@/components/ui/Btn.vue";
import Dialog from "@/components/ui/Dialog.vue";
import Spinner from "@/components/ui/Spinner.vue";
import {
    getOrCreateGamificationContext,
    getValidActions,
    listActions,
    createAction,
    updateAction,
    deleteAction,
    listBadges,
    createBadge,
    updateBadge,
    deleteBadge,
    listGoals,
    createGoal,
    updateGoal,
    deleteGoal,
    listGoalLevels,
    createGoalLevel,
    updateGoalLevel,
    deleteGoalLevel,
    type ValidAction,
    type ActionDefinition,
    type BadgeDefinitionManage,
    type GoalManage,
    type GoalLevel,
} from "@/api/gamification_management";

export default defineComponent({
    name: "CourseGamification",
    components: { Btn, Dialog, Spinner },
    mixins: [courseIdMixin],

    data() {
        return {
            loading: true,
            creating: false,
            saving: false,
            deleting: false,
            contextId: null as string | null,
            activeTab: "actions" as "actions" | "badges" | "goals",

            validActions: [] as ValidAction[],
            actions: [] as ActionDefinition[],
            badges: [] as BadgeDefinitionManage[],
            goals: [] as GoalManage[],

            // Action dialog
            showActionDialog: false,
            editingAction: null as ActionDefinition | null,
            actionForm: { action_code: "", reputation_awarded: 10, badges_awarded: [] as string[] },

            // Badge dialog
            showBadgeDialog: false,
            editingBadge: null as BadgeDefinitionManage | null,
            badgeForm: { name: "", icon: "military_tech", description: "" },

            // Goal dialog
            showGoalDialog: false,
            editingGoal: null as GoalManage | null,
            goalForm: { name: "" },

            // Level dialog
            showLevelDialog: false,
            editingLevel: null as GoalLevel | null,
            currentGoalForLevel: null as GoalManage | null,
            levelForm: {
                level_value: 1,
                reputation_awarded: 50,
                requirements: [] as Array<{ action_definition_id: string; amount: number }>,
                badges_awarded: [] as string[],
            },

            // Delete dialog
            showDeleteDialog: false,
            deleteMessage: "",
            deletePending: null as (() => Promise<void>) | null,
        };
    },

    computed: {
        tabs() {
            return [
                { key: "actions", label: this.$t("course_gamification.tab_actions"), icon: "bolt" },
                { key: "badges", label: this.$t("course_gamification.tab_badges"), icon: "military_tech" },
                { key: "goals", label: this.$t("course_gamification.tab_goals"), icon: "flag" },
            ];
        },
        availableActionsForForm(): ValidAction[] {
            if (this.editingAction) return this.validActions;
            const usedCodes = new Set(this.actions.map(a => a.action_code));
            return this.validActions.filter(va => !usedCodes.has(va.code));
        },
    },

    async mounted() {
        await this.loadPage();
    },

    methods: {
        async loadPage() {
            this.loading = true;
            try {
                const ctx = await getOrCreateGamificationContext(this.courseId);
                if (ctx?.id) {
                    this.contextId = ctx.id;
                    await this.loadAll();
                }
            } catch {
                this.contextId = null;
            } finally {
                this.loading = false;
            }
        },

        async setupContext() {
            this.creating = true;
            try {
                const ctx = await getOrCreateGamificationContext(this.courseId);
                this.contextId = ctx.id;
                await this.loadAll();
            } finally {
                this.creating = false;
            }
        },

        async loadAll() {
            const [validActs, acts, bgs, gls] = await Promise.all([
                getValidActions(),
                listActions(this.contextId!),
                listBadges(this.contextId!),
                listGoals(this.contextId!),
            ]);
            this.validActions = validActs;
            this.actions = acts;
            this.badges = bgs;
            // Load levels for each goal
            this.goals = await Promise.all(
                gls.map(async (g: GoalManage) => {
                    const levels = await listGoalLevels(this.contextId!, g.id);
                    return { ...g, levels };
                })
            );
        },

        actionLabel(code: string): string {
            return this.validActions.find(v => v.code === code)?.label ?? code;
        },

        badgeById(id: string): BadgeDefinitionManage | undefined {
            return this.badges.find(b => b.id === id);
        },

        toggleBadge(list: string[], id: string) {
            const idx = list.indexOf(id);
            if (idx >= 0) list.splice(idx, 1);
            else list.push(id);
        },

        sortedLevels(goal: GoalManage): GoalLevel[] {
            return [...(goal.levels || [])].sort((a, b) => a.level_value - b.level_value);
        },

        // ── Actions ──
        openActionDialog(act: ActionDefinition | null) {
            this.editingAction = act;
            this.actionForm = act
                ? { action_code: act.action_code, reputation_awarded: act.reputation_awarded, badges_awarded: [...(act.badges_awarded || [])] }
                : { action_code: "", reputation_awarded: 10, badges_awarded: [] };
            this.showActionDialog = true;
        },

        async saveAction() {
            this.saving = true;
            try {
                if (this.editingAction) {
                    const updated = await updateAction(this.contextId!, this.editingAction.id, {
                        reputation_awarded: this.actionForm.reputation_awarded,
                        badges_awarded: this.actionForm.badges_awarded,
                    });
                    const idx = this.actions.findIndex(a => a.id === this.editingAction!.id);
                    if (idx >= 0) this.actions[idx] = updated;
                } else {
                    const created = await createAction(this.contextId!, this.actionForm);
                    this.actions.push(created);
                }
                this.showActionDialog = false;
            } finally {
                this.saving = false;
            }
        },

        confirmDeleteAction(act: ActionDefinition) {
            this.deleteMessage = this.$t("course_gamification.confirm_delete_action", {
                name: this.actionLabel(act.action_code),
            });
            this.deletePending = async () => {
                await deleteAction(this.contextId!, act.id);
                this.actions = this.actions.filter(a => a.id !== act.id);
            };
            this.showDeleteDialog = true;
        },

        // ── Badges ──
        openBadgeDialog(badge: BadgeDefinitionManage | null) {
            this.editingBadge = badge;
            this.badgeForm = badge
                ? { name: badge.name, icon: badge.icon, description: badge.description }
                : { name: "", icon: "military_tech", description: "" };
            this.showBadgeDialog = true;
        },

        async saveBadge() {
            this.saving = true;
            try {
                if (this.editingBadge) {
                    const updated = await updateBadge(this.contextId!, this.editingBadge.id, this.badgeForm);
                    const idx = this.badges.findIndex(b => b.id === this.editingBadge!.id);
                    if (idx >= 0) this.badges[idx] = updated;
                } else {
                    const created = await createBadge(this.contextId!, this.badgeForm);
                    this.badges.push(created);
                }
                this.showBadgeDialog = false;
            } finally {
                this.saving = false;
            }
        },

        confirmDeleteBadge(badge: BadgeDefinitionManage) {
            this.deleteMessage = this.$t("course_gamification.confirm_delete_badge", { name: badge.name });
            this.deletePending = async () => {
                await deleteBadge(this.contextId!, badge.id);
                this.badges = this.badges.filter(b => b.id !== badge.id);
            };
            this.showDeleteDialog = true;
        },

        // ── Goals ──
        openGoalDialog(goal: GoalManage | null) {
            this.editingGoal = goal;
            this.goalForm = { name: goal?.name ?? "" };
            this.showGoalDialog = true;
        },

        async saveGoal() {
            this.saving = true;
            try {
                if (this.editingGoal) {
                    const updated = await updateGoal(this.contextId!, this.editingGoal.id, this.goalForm);
                    const idx = this.goals.findIndex(g => g.id === this.editingGoal!.id);
                    if (idx >= 0) this.goals[idx] = { ...this.goals[idx], ...updated };
                } else {
                    const created = await createGoal(this.contextId!, this.goalForm);
                    this.goals.push({ ...created, levels: [] });
                }
                this.showGoalDialog = false;
            } finally {
                this.saving = false;
            }
        },

        confirmDeleteGoal(goal: GoalManage) {
            this.deleteMessage = this.$t("course_gamification.confirm_delete_goal", { name: goal.name });
            this.deletePending = async () => {
                await deleteGoal(this.contextId!, goal.id);
                this.goals = this.goals.filter(g => g.id !== goal.id);
            };
            this.showDeleteDialog = true;
        },

        // ── Levels ──
        openLevelDialog(goal: GoalManage, level: GoalLevel | null) {
            this.currentGoalForLevel = goal;
            this.editingLevel = level;
            this.levelForm = level
                ? {
                    level_value: level.level_value,
                    reputation_awarded: level.reputation_awarded,
                    requirements: (level.requirements || []).map(r => ({
                        action_definition_id: r.action_definition_id,
                        amount: r.amount,
                    })),
                    badges_awarded: [...(level.badges_awarded || [])],
                }
                : {
                    level_value: (goal.levels?.length ?? 0) + 1,
                    reputation_awarded: 50,
                    requirements: [],
                    badges_awarded: [],
                };
            this.showLevelDialog = true;
        },

        addRequirement() {
            this.levelForm.requirements.push({ action_definition_id: "", amount: 1 });
        },

        removeRequirement(index: number) {
            this.levelForm.requirements.splice(index, 1);
        },

        async saveLevel() {
            if (!this.currentGoalForLevel) return;
            this.saving = true;
            try {
                const payload = {
                    level_value: this.levelForm.level_value,
                    reputation_awarded: this.levelForm.reputation_awarded,
                    requirements: this.levelForm.requirements.filter(
                        r => r.action_definition_id && r.amount > 0
                    ),
                    badges_awarded: this.levelForm.badges_awarded,
                };
                const goalIdx = this.goals.findIndex(g => g.id === this.currentGoalForLevel!.id);
                if (this.editingLevel) {
                    const updated = await updateGoalLevel(
                        this.contextId!, this.currentGoalForLevel.id, this.editingLevel.id, payload
                    );
                    if (goalIdx >= 0) {
                        const lvlIdx = this.goals[goalIdx].levels.findIndex(l => l.id === this.editingLevel!.id);
                        if (lvlIdx >= 0) this.goals[goalIdx].levels[lvlIdx] = updated;
                    }
                } else {
                    const created = await createGoalLevel(
                        this.contextId!, this.currentGoalForLevel.id, payload
                    );
                    if (goalIdx >= 0) this.goals[goalIdx].levels.push(created);
                }
                this.showLevelDialog = false;
            } finally {
                this.saving = false;
            }
        },

        confirmDeleteLevel(goal: GoalManage, level: GoalLevel) {
            this.deleteMessage = this.$t("course_gamification.confirm_delete_level", {
                level: level.level_value,
                goal: goal.name,
            });
            this.deletePending = async () => {
                await deleteGoalLevel(this.contextId!, goal.id, level.id);
                const goalIdx = this.goals.findIndex(g => g.id === goal.id);
                if (goalIdx >= 0) {
                    this.goals[goalIdx].levels = this.goals[goalIdx].levels.filter(l => l.id !== level.id);
                }
            };
            this.showDeleteDialog = true;
        },

        // ── Delete ──
        async performDelete() {
            if (!this.deletePending) return;
            this.deleting = true;
            try {
                await this.deletePending();
                this.showDeleteDialog = false;
                this.deletePending = null;
            } finally {
                this.deleting = false;
            }
        },
    },
});
